import { Box, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { SHRED_COLORS, SHRED_COPY, SHRED_FONTS, SHRED_PODIUM_METALS } from '../constants';
import { formatPct, getFairnessSummary, getPodiumEntries, getTopRookie } from '../helpers';
import { CountUp, LevelChip, MemberAvatar, Panel, SectionTitle, focusRing, pctColor } from './shared';

// Visual order is silver, gold, bronze so the champion sits in the middle and tallest.
const VISUAL_ORDER = [1, 0, 2];
const PEDESTAL_HEIGHT = { 0: 120, 1: 84, 2: 60 };

const PodiumSlot = ({ entry, index, reduced, isMine }) => {
  const metal = SHRED_PODIUM_METALS[index];
  const champion = index === 0;
  return (
    <Box
      className="shred-motion"
      sx={{
        order: { xs: 0 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-end',
        minWidth: 0,
        animation: reduced ? 'none' : `shredRise 700ms cubic-bezier(0.2,0.8,0.2,1) ${index === 0 ? 300 : index === 1 ? 100 : 200}ms both`,
      }}
    >
      {entry ? (
        <>
          <Box sx={{ position: 'relative', mb: 1 }}>
            {champion && (
              <Box aria-hidden="true" sx={{ position: 'absolute', top: -22, left: 0, right: 0, textAlign: 'center', fontSize: 24 }}>
                👑
              </Box>
            )}
            <MemberAvatar id={entry.id} name={entry.username} size={champion ? 84 : 64} ring={metal.color} />
          </Box>
          <Box
            component={RouterLink}
            to={`/member/${encodeURIComponent(entry.username)}`}
            sx={{ ...focusRing, textDecoration: 'none', maxWidth: '100%' }}
          >
            <Typography
              noWrap
              sx={{
                fontFamily: SHRED_FONTS.display,
                fontWeight: 700,
                fontSize: { xs: champion ? 20 : 17, sm: champion ? 26 : 20 },
                color: isMine ? SHRED_COLORS.gold : SHRED_COLORS.text,
                maxWidth: '100%',
              }}
            >
              {entry.username}
            </Typography>
          </Box>
          <LevelChip level={entry.level} sx={{ mt: 0.5 }} />
          <Typography
            sx={{
              mt: 0.75,
              fontFamily: SHRED_FONTS.numeric,
              fontSize: { xs: champion ? 20 : 16, sm: champion ? 26 : 20 },
              color: pctColor(entry.improvementPct),
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            <CountUp value={entry.improvementPct} format={(v) => formatPct(v)} reduced={reduced} />
          </Typography>
        </>
      ) : (
        <Typography sx={{ color: SHRED_COLORS.muted, fontFamily: SHRED_FONTS.body, fontSize: 14, mb: 1 }}>
          {SHRED_COPY.PODIUM_EMPTY}
        </Typography>
      )}
      <Box
        sx={{
          mt: 1.25,
          width: '100%',
          height: PEDESTAL_HEIGHT[index],
          borderRadius: '10px 10px 0 0',
          background: `linear-gradient(180deg, ${metal.color}33 0%, ${SHRED_COLORS.panel} 100%)`,
          borderTop: `3px solid ${metal.color}`,
          display: 'grid',
          placeItems: 'center',
          fontFamily: SHRED_FONTS.display,
          fontWeight: 800,
          fontSize: 44,
          color: metal.color,
        }}
      >
        <span aria-label={`Rank ${metal.rank}`}>{metal.rank}</span>
      </Box>
    </Box>
  );
};

const Podium = ({ entries, isMine, reduced }) => {
  const podium = getPodiumEntries(entries);
  const fairness = getFairnessSummary(entries);
  const rookie = getTopRookie(entries);
  return (
    <Box component="section" aria-labelledby="shred-podium-title">
      <SectionTitle kicker="Top 3" title={SHRED_COPY.PODIUM_TITLE} id="shred-podium-title" />
      <Panel sx={{ p: { xs: 2, sm: 3 }, background: `linear-gradient(180deg, ${SHRED_COLORS.panelRaised}, ${SHRED_COLORS.panel})` }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: { xs: 1, sm: 3 }, alignItems: 'end' }}>
          {VISUAL_ORDER.map((index) => (
            <PodiumSlot
              key={index}
              entry={podium[index]}
              index={index}
              reduced={reduced}
              isMine={podium[index] ? isMine(podium[index]) : false}
            />
          ))}
        </Box>
        {rookie && (
          <Box
            sx={{
              mt: 2,
              p: 1.25,
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              borderRadius: '10px',
              background: 'rgba(181,123,255,0.12)',
              border: '1px solid rgba(181,123,255,0.35)',
              minWidth: 0,
            }}
          >
            <Box aria-hidden="true" sx={{ fontSize: 22 }}>🎃</Box>
            <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: 11, letterSpacing: '0.14em', color: '#d9bcff', flex: 'none' }}>
              {SHRED_COPY.ROOKIE_STAR_TITLE.toUpperCase()}
            </Typography>
            <Typography noWrap sx={{ fontFamily: SHRED_FONTS.body, fontWeight: 600, fontSize: 15, minWidth: 0 }}>
              {rookie.username}
            </Typography>
            <LevelChip level={rookie.level} />
            <Typography sx={{ ml: 'auto', fontFamily: SHRED_FONTS.numeric, fontSize: 15, color: pctColor(rookie.improvementPct) }}>
              {formatPct(rookie.improvementPct)}
            </Typography>
          </Box>
        )}
        {fairness && (
          <Box
            sx={{
              mt: 2.5,
              p: 1.75,
              borderRadius: '10px',
              background: SHRED_COLORS.heatSoft,
              border: `1px solid ${SHRED_COLORS.line}`,
            }}
          >
            <Typography sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 700, fontSize: 18, textTransform: 'uppercase', color: SHRED_COLORS.gold }}>
              {SHRED_COPY.FAIRNESS_TITLE}
            </Typography>
            <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: 14, color: SHRED_COLORS.text, mt: 0.25 }}>
              The podium is Lv {fairness.podiumLevels.join(' · Lv ')}.{' '}
              {fairness.lowest.level < fairness.medianLevel
                ? `${fairness.lowest.username} is level ${fairness.lowest.level}, well under the median of ${fairness.medianLevel}, and still on top. Beat your own baseline and you can win from anywhere.`
                : 'Beat your own baseline and you can win from any level.'}
            </Typography>
          </Box>
        )}
      </Panel>
    </Box>
  );
};

export default Podium;
